import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, Video, VideoOff } from 'lucide-react'

import type { Camera, CameraStreamState } from './camera-types'
import { cameraLiveUrl, cameraSnapshotUrl } from '@/lib/api/cameras-api'
import { Button } from '@/components/ui/button'
import { useHlsPlayer } from '@/hooks/use-hls-player'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * `snapshot` = một ảnh tĩnh, tự làm mới theo chu kỳ (thẻ trong lưới).
 * `live` = luồng HLS liên tục (khung xem trực tiếp).
 */
export type CameraViewMode = 'snapshot' | 'live'

/** Chu kỳ làm mới ảnh của thẻ. Đủ để "ngó qua" mà không kéo băng thông liên tục. */
export const SNAPSHOT_REFRESH_MS = 20_000

interface Props {
    camera: Camera
    /** Khung hình chỉ được kéo khi component đang hiển thị. */
    active: boolean
    mode: CameraViewMode
    onStateChange?: (state: CameraStreamState) => void
    className?: string
    /** Hiện nút tải lại khi lỗi (khung xem lớn) hay không (thẻ nhỏ). */
    showReload?: boolean
}

/**
 * Khung hình của một camera — ảnh tĩnh hoặc luồng trực tiếp.
 *
 * Cả hai đều gọi relay của ERP, xác thực bằng cookie `access_token`; tài khoản
 * Basic Auth của camera không bao giờ xuống trình duyệt. Ảnh tĩnh là thẻ
 * `<img>` trỏ vào endpoint snapshot; luồng trực tiếp là `<video>` phát HLS.
 *
 * `active=false` thì KHÔNG render gì: luồng trực tiếp gọi máy chủ camera vài
 * lần mỗi giây, để trình phát sống sau khi rời tab là kéo băng thông từ vườn
 * cho mỗi người đang mở màn hộ.
 */
export function CameraStreamView({
    camera,
    active,
    mode,
    onStateChange,
    className,
    showReload = false,
}: Props) {
    // Tăng mỗi lần cần lấy khung mới (bấm tải lại, hoặc tới chu kỳ làm mới).
    // URL phải khác đi, không thì trình duyệt trả lại ảnh cũ trong cache.
    const [attempt, setAttempt] = useState(0)
    const [state, setState] = useState<CameraStreamState>(
        camera.has_stream ? 'loading' : 'unconfigured',
    )
    // Giữ callback trong ref thay vì đưa vào deps: prop này thường là một hàm
    // mới mỗi lần render ở call-site, đưa vào deps là effect chạy lại liên tục.
    // Gán trong effect (không phải giữa lúc render) để render vẫn thuần tuý.
    const notify = useRef(onStateChange)
    useEffect(() => {
        notify.current = onStateChange
    }, [onStateChange])

    useEffect(() => {
        const next: CameraStreamState = !camera.has_stream
            ? 'unconfigured'
            : active
              ? 'loading'
              : 'paused'
        setState(next)
        notify.current?.(next)
    }, [active, camera.has_stream, camera.stream_token, attempt])

    // Mở lại sau khi bị ngắt: đổi URL để chắc chắn là một kết nối MỚI, không
    // bám vào response cũ đã bị huỷ lúc ngắt. Đổi ngay trong lúc render (không
    // chờ effect): chờ effect là thẻ ảnh kịp mount với URL cũ, mở một kết nối
    // tới camera rồi bị huỷ ngay khi URL mới tới.
    const [prevActive, setPrevActive] = useState(active)
    if (active !== prevActive) {
        setPrevActive(active)
        if (active) setAttempt((n) => n + 1)
    }

    // Ảnh tĩnh tự làm mới; luồng trực tiếp thì không (nó đã liên tục sẵn).
    // Dừng hẳn khi tab bị ẩn: không ai đang nhìn thì không kéo ảnh từ vườn.
    useEffect(() => {
        if (mode !== 'snapshot' || !active || !camera.has_stream) return

        const tick = () => {
            if (document.hidden) return
            setAttempt((n) => n + 1)
        }
        const timer = setInterval(tick, SNAPSHOT_REFRESH_MS)
        return () => clearInterval(timer)
    }, [mode, active, camera.has_stream])

    const src = useMemo(() => {
        if (!camera.has_stream || !active) return null
        if (mode === 'live') return cameraLiveUrl(camera.id)
        return cameraSnapshotUrl(
            camera.id,
            `${camera.updated_at ?? ''}-${attempt}`,
        )
    }, [active, attempt, camera.has_stream, camera.id, camera.updated_at, mode])

    const update = (next: CameraStreamState) => {
        setState(next)
        notify.current?.(next)
    }

    return (
        <div
            className={twJoin(
                'relative flex items-center justify-center overflow-hidden bg-neutral-900',
                className,
            )}
        >
            {src && mode === 'live' ? (
                // key theo lần thử: tải lại hay mở lại là dựng trình phát MỚI,
                // xin playlist gốc (và chữ ký của máy chủ camera) mới.
                <LiveVideo
                    key={`${camera.updated_at ?? ''}-${attempt}`}
                    src={src}
                    label={camera.name}
                    playing={state === 'playing'}
                    onPlaying={() => update('playing')}
                    onError={() => update('error')}
                />
            ) : src ? (
                <img
                    key={src}
                    src={src}
                    alt={camera.name}
                    className={twJoin(
                        'size-full object-cover',
                        state === 'playing' ? 'opacity-100' : 'opacity-0',
                    )}
                    onLoad={() => update('playing')}
                    onError={() => update('error')}
                />
            ) : null}

            {state !== 'playing' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
                    {state === 'error' ? (
                        <VideoOff className="size-7 text-white/40" />
                    ) : (
                        <Video className="size-7 text-white/25" />
                    )}
                    <p className="max-w-[34ch] text-xs text-white/70">
                        {state === 'unconfigured'
                            ? m.grower_area_camera_missing_stream()
                            : state === 'error'
                              ? m.grower_area_camera_stream_error()
                              : state === 'paused'
                                ? m.grower_area_camera_paused()
                                : m.grower_area_camera_stream_loading()}
                    </p>
                    {showReload && state === 'error' && (
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setAttempt((n) => n + 1)}
                        >
                            <RefreshCw className="size-3.5" />
                            {m.grower_area_camera_stream_reload()}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

interface LiveVideoProps {
    src: string
    label: string
    playing: boolean
    onPlaying: () => void
    onError: () => void
}

/** Thẻ `<video>` phát luồng HLS qua relay; tắt tiếng để trình duyệt cho tự phát. */
function LiveVideo({
    src,
    label,
    playing,
    onPlaying,
    onError,
}: LiveVideoProps) {
    const videoRef = useHlsPlayer(src, { onPlaying, onError })
    return (
        <video
            ref={videoRef}
            aria-label={label}
            muted
            autoPlay
            playsInline
            disablePictureInPicture
            className={twJoin(
                'size-full object-cover',
                playing ? 'opacity-100' : 'opacity-0',
            )}
        />
    )
}
